import { useState, useRef, useMemo } from "react";

import { Row, Col } from "react-bootstrap";
import { connect } from "react-redux";
import Switch from "react-bootstrap-switch";
import useCustomTableFilters from "hooks/useCustomTableFilters";
import BulkActionsTable from "views/merchandise/tables/BulkActionsTable";
import { formatCurrency } from "utils/formatNumber";
import { Button } from "components/core/basic/Button";
import { css } from "../../../styled-system/css";

const PriceDetails = ({ optData, marketPlace }) => {
  const [daysWithPrice, setDaysWithPrice] = useState(optData?.pricing_org?.days_with_price);

  const TABLE_ID = "priceHistoryDetailsTable";
  const tableRef = useRef();

  const filterProps = useCustomTableFilters(TABLE_ID);

  const tableData = useMemo(() => {
    return (
      optData?.pricing_data
        ?.filter((r) => r.num_days_at_price !== 0 && r.num_days_at_price >= daysWithPrice)
        ?.map((d) => ({
          ...d,
          total_cogs: d.cogs,
          cr: d?.qty_sold > 0 && d?.page_views > 0 ? d.qty_sold / d.page_views : 0,
        })) ?? []
    );
  }, [optData, daysWithPrice]);

  const downloadCSV = () => {
    const data = [
      [
        "price",
        "day_at_price",
        "quantity_sold",
        "daily_units",
        "monthly_units",
        "cogs",
        "margin",
        "monthly_profit",
        "page_views",
        "cr",
      ],
    ];

    tableData.forEach((row) => {
      data.push([
        row.price ? row.price.toFixed(2) : 0,
        row.num_days_at_price ? row.num_days_at_price.toFixed(2) : 0,
        row.qty_sold ? row.qty_sold.toFixed(2) : 0,
        row.avg_daily_sales ? row.avg_daily_sales.toFixed(2) : 0,
        row.avg_monthly_sales ? row.avg_monthly_sales.toFixed(2) : 0,
        row.cogs ? row.cogs.toFixed(2) : 0,
        row.org_profit ? row.org_profit.toFixed(2) : 0,
        row.monthly_profit ? row.monthly_profit.toFixed(2) : 0,
        row.page_views ? row.page_views.toFixed(2) : 0,
        row.cr ? row.cr.toFixed(4) : 0,
      ]);
    });

    const csvContent = data.map((e) => e.join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "price_history_download.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Row>
        <Col className="text-end pe-3 pb-2">
          <Switch
            onText={``}
            offText={``}
            value={daysWithPrice !== 0}
            onColor="#FFFFFF"
            onChange={() => {
              if (daysWithPrice === 0) {
                setDaysWithPrice(optData.pricing_org.days_with_price - 1);
              } else {
                setDaysWithPrice(0);
              }
            }}
          />
          {daysWithPrice !== 0 ?
            <span className="ps-3">Show prices with less than {optData.pricing_org.days_with_price} days of data</span>
          : <span className="ps-3">Hide prices with less than {optData.pricing_org.days_with_price} days of data</span>}
        </Col>
      </Row>
      <BulkActionsTable
        tableId={TABLE_ID}
        tableRef={tableRef}
        filterWidget={true}
        hideFilterToggleIcon={true}
        {...filterProps}
        columnSpecs={[
          {
            key: "product_price",
            compareKey: null,
            options: {
              className: "d-flex text-start align-items-center ps-3",
              metric: {
                id: "price",
                format: "currency",
                accessorId: "price",
                accessor: (d) => {
                  return d?.price;
                },
                name: "Price",
                width: "100px",
              },
            },
          },
          {
            key: "num_days_at_price",
            compareKey: null,
            options: {
              metric: {
                id: "num_days_at_price",
                format: "number",
                accessor: (d) => {
                  return d?.num_days_at_price;
                },
                name: "Days at Price",
              },
            },
          },
          {
            key: "qty_sold",
            compareKey: null,
            options: {
              metric: {
                id: "qty_sold",
                format: "number",
                accessor: (d) => {
                  return d?.qty_sold;
                },
                name: "Quantity Sold",
              },
            },
          },
          {
            key: "avg_daily_sales",
            compareKey: null,
            options: {
              metric: {
                id: "avg_daily_sales",
                format: "number",
                accessor: (d) => {
                  return d?.avg_daily_sales;
                },
                name: "Daily Units",
                decimalPlaces: 2,
              },
            },
          },
          {
            key: "avg_monthly_sales",
            compareKey: null,
            options: {
              metric: {
                id: "avg_mnthly_sales",
                format: "number",
                accessor: (d) => {
                  return d?.avg_monthly_sales;
                },
                name: "Monthly Units",
                decimalPlaces: 2,
              },
            },
          },
          {
            key: "cogs",
            compareKey: null,
            options: {
              metric: {
                id: "cogs",
                format: "currency",
                accessor: (d) => {
                  return d?.cogs;
                },
                name: "COGS",
              },
            },
          },
          {
            key: "org_profit",
            compareKey: null,
            options: {
              metric: {
                id: "org_profit",
                format: "currency",
                accessor: (d) => {
                  return d?.org_profit;
                },
                formatter: (v) => formatCurrency(v, marketPlace.marketPlace),
                name: "Margin",
              },
            },
          },
          {
            key: "monthly_profit",
            compareKey: null,
            options: {
              metric: {
                id: "monthly_profit",
                format: "currency",

                accessor: (d) => {
                  return d?.monthly_profit;
                },
                name: "Monthly Profit",
              },
            },
          },
          {
            key: "page_views",
            compareKey: null,
            options: {
              metric: {
                id: "page_views",
                format: "currency",

                accessor: (d) => {
                  return d?.page_views;
                },
                name: "Page Views",
                checked: false,
              },
            },
          },
          {
            key: "cr",
            compareKey: null,
            options: {
              metric: {
                id: "cr",
                format: "percent",

                accessor: (d) => {
                  return d.cr;
                },
                name: "CR",
                checked: false,
              },
            },
          },

          {
            key: "vat",
            compareKey: null,
            options: {
              checked: false,
              metric: {
                id: "vat",
                format: "currency",

                accessor: (d) => {
                  return d?.vat;
                },
                name: "VAT",
                checked: false,
              },
            },
          },
          {
            key: "landed_cogs",
            compareKey: null,
            options: {
              checked: false,
              metric: {
                id: "landed_cogs",
                format: "currency",
                accessor: (d) => {
                  return d?.landed_cogs;
                },
                name: "Landed Cost",
              },
            },
          },
          {
            key: "ship_cost",
            compareKey: null,
            options: {
              checked: false,
              metric: {
                id: "ship_cost",
                format: "currency",
                accessor: (d) => {
                  return d?.ship_cost;
                },
                name: "Shipping Cost",
              },
            },
          },
          {
            key: "closing_fees",
            compareKey: null,
            options: {
              checked: false,
              metric: {
                id: "closing_fees",
                format: "currency",
                accessor: (d) => {
                  return d?.closing_fees;
                },
                name: "Closing Fee",
              },
            },
          },
          {
            key: "referral_fees",
            compareKey: null,
            options: {
              checked: false,
              metric: {
                id: "referral_fees",
                format: "currency",
                accessor: (d) => {
                  return d?.referral_fees;
                },
                name: "Referral Fee",
              },
            },
          },
        ]}
        data={tableData}
        className="pricing-table"
        selectionKey="price"
        isExpandable={false}
        isSelectable={false}
        hideSearchFilter={true}
        defaultPageSize={10}
        tableRowDescription={"Price"}
        groupMetrics={false}
      />
      <Button
        onPress={() => {
          downloadCSV();
        }}
        className={css({ marginLeft: "5px" })}
      >
        {"Download CSV"}
      </Button>
    </>
  );
};

const mapStateToProps = (state) => ({
  marketPlace: state.marketPlace,
});

export default connect(mapStateToProps)(PriceDetails);
